import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";

import { Button, Label } from "@fronterahealth/frontera-ui-components";

import {
  createEvalMultiModelTest,
  getListEvalMultiModelTestsQueryKey,
  suggestMultiModelTestProfiles,
  useListAvailableModels,
  useListTemplates,
} from "@api/rest/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { REPORT_TYPE_LABELS, TemplateStatus } from "@pages/Templates/constants";

const SELECT_CLASSES =
  "w-full text-sm border border-border-subtle rounded px-2 py-1.5 bg-bg-default " +
  "focus:outline-none focus:ring-1 focus:ring-limestone-900 disabled:opacity-40";

interface Props {
  onStarted: (testId: number) => void;
}

/** Kickoff for a multi-model test (PROC-221): pick a subset of templates, the
 *  candidate models, and go. Tests span orgs — the picker lists every
 *  published template and the backend resolves each one's org. Profiles
 *  are auto-suggested per template by slot coverage (server-ranked) and
 *  overridable. The POST only creates the grid — runs fan out as the
 *  results screen polls. */
export const MultiModelTestSetupCard: FC<Props> = ({ onStarted }) => {
  const queryClient = useQueryClient();
  const [templateIds, setTemplateIds] = useState<number[]>([]);
  const [profileByTemplate, setProfileByTemplate] = useState<Record<number, number>>({});
  const [candidateModels, setCandidateModels] = useState<string[]>([]);
  // Raw string so the box can be cleared — Number("") is 0, and a
  // number-typed state writes that 0 straight back into the input.
  const [name, setName] = useState("");
  const [starting, setStarting] = useState(false);

  const { data: modelsResponse } = useListAvailableModels();
  const models = modelsResponse?.status === 200 ? modelsResponse.data : null;
  const candidates = useMemo(
    () => (models?.models ?? []).filter((m) => !m.is_default).sort((a, b) => a.label.localeCompare(b.label)),
    [models],
  );

  // Server-side published filter keeps the full cross-org list inside
  // the endpoint's one-page cap (page_size max 100).
  const { data: templatesResponse } = useListTemplates({ status: TemplateStatus.PUBLISHED, page_size: 100 });
  const templates = ((templatesResponse?.status === 200 ? templatesResponse.data.results : null) ?? [])
    .filter((t) => t.status === TemplateStatus.PUBLISHED)
    .sort((a, b) => a.name.localeCompare(b.name));
  // Client-side filter over name / report-type label / id — selections
  // persist while filtered out (checkbox state lives in templateIds).
  const [templateSearch, setTemplateSearch] = useState("");
  const query = templateSearch.trim().toLowerCase();
  const visibleTemplates = query
    ? templates.filter((t) =>
        `${t.name} ${REPORT_TYPE_LABELS[t.report_type as keyof typeof REPORT_TYPE_LABELS] ?? t.report_type} #${t.id}`
          .toLowerCase()
          .includes(query),
      )
    : templates;

  // Server-ranked profile coverage for the selected templates; the best
  // option per template is pre-selected, the dropdown overrides.
  const { data: suggestions } = useQuery({
    queryKey: ["multi-model-test-profile-suggestions", [...templateIds].sort().join(",")],
    enabled: templateIds.length > 0,
    queryFn: async () => {
      const resp = await suggestMultiModelTestProfiles({ template_ids: templateIds.join(",") });
      return resp.status === 200 ? resp.data.templates : [];
    },
  });

  const toggleTemplate = (id: number) => {
    setTemplateIds((ids) => (ids.includes(id) ? ids.filter((t) => t !== id) : [...ids, id]));
  };
  const toggleCandidate = (id: string) => {
    setCandidateModels((ms) => (ms.includes(id) ? ms.filter((m) => m !== id) : [...ms, id]));
  };

  const profileFor = (templateId: number): number | null => {
    if (profileByTemplate[templateId]) return profileByTemplate[templateId];
    const suggestion = suggestions?.find((s) => s.template_id === templateId);
    return suggestion?.suggested?.learner_test_profile_id ?? null;
  };

  const ready =
    templateIds.length > 0 && candidateModels.length > 0 && templateIds.every((id) => profileFor(id) !== null);

  const handleStart = async () => {
    setStarting(true);
    try {
      const resp = await createEvalMultiModelTest({
        name,
        candidate_models: candidateModels,
        // No organization_id — the backend resolves each template's org.
        items: templateIds.map((template_id) => ({
          template_id,
          learner_test_profile_id: profileFor(template_id),
        })),
      });
      if (resp.status !== 200) {
        notifyError((resp.data as { detail?: string })?.detail ?? "Could not create the test.");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: getListEvalMultiModelTestsQueryKey() });
      onStarted(resp.data.id);
    } catch {
      notifyError("Could not create the test.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <section className="rounded-lg border border-border-subtle bg-bg-surface p-4">
      <Label size="small" colorType="secondary" className="uppercase tracking-wide">
        Multi-model testing
      </Label>
      <p className="mt-1 text-sm text-text-secondary">
        Run a subset of templates against several candidate models at once; pick the upgrade by the judge's pass rate.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <Label size="small" colorType="primary">
            Templates
          </Label>
          <input
            type="search"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            placeholder="Search templates…"
            aria-label="Search templates"
            className="mt-1 w-full rounded border border-border-subtle bg-bg-default px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-limestone-900"
          />
          <div className="mt-1 max-h-56 overflow-y-auto rounded border border-border-subtle bg-bg-default p-2">
            {visibleTemplates.length === 0 ? (
              <p className="text-xs text-text-tertiary">
                {templates.length === 0 ? "No published templates." : "No templates match the search."}
              </p>
            ) : (
              visibleTemplates.map((t) => (
                <label key={t.id} className="flex items-center gap-2 py-0.5 text-sm text-text-primary">
                  <input type="checkbox" checked={templateIds.includes(t.id)} onChange={() => toggleTemplate(t.id)} />
                  {t.name}
                  {/* Names repeat across report types and orgs — the type + id disambiguate. */}
                  <span className="text-xs text-text-tertiary">
                    {REPORT_TYPE_LABELS[t.report_type as keyof typeof REPORT_TYPE_LABELS] ?? t.report_type} · #{t.id}
                  </span>
                </label>
              ))
            )}
          </div>
          {templateIds.map((id) => {
            const suggestion = suggestions?.find((s) => s.template_id === id);
            if (!suggestion) return null;
            const template = templates.find((t) => t.id === id);
            const typeLabel = template
              ? (REPORT_TYPE_LABELS[template.report_type as keyof typeof REPORT_TYPE_LABELS] ?? template.report_type)
              : null;
            const headerLabel = `${suggestion.template_name}${typeLabel ? ` (${typeLabel})` : ""} — profile`;
            if (suggestion.profiles.length === 0) {
              return (
                <div key={id} className="mt-2">
                  <span className="text-xs text-text-secondary">{headerLabel}</span>
                  <p className="mt-1 text-xs text-text-error-primary">
                    No learner test profile can run this template — create one for its org (or a global one) first.
                  </p>
                </div>
              );
            }
            return (
              <div key={id} className="mt-2">
                <span className="text-xs text-text-secondary">{headerLabel}</span>
                <select
                  className={SELECT_CLASSES}
                  value={profileFor(id) ?? ""}
                  onChange={(e) => setProfileByTemplate((m) => ({ ...m, [id]: Number(e.target.value) }))}
                >
                  {suggestion.profiles.map((p) => (
                    <option key={p.learner_test_profile_id} value={p.learner_test_profile_id}>
                      {p.profile_name} — runs {p.runnable}/{suggestion.total_slots}
                      {p.learner_test_profile_id === suggestion.suggested?.learner_test_profile_id
                        ? " (suggested)"
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <Label size="small" colorType="primary">
              Candidate models
            </Label>
            <div className="mt-1 rounded border border-border-subtle bg-bg-default p-2">
              {candidates.map((m) => (
                <label key={m.id} className="flex items-center gap-2 py-0.5 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={candidateModels.includes(m.id)}
                    onChange={() => toggleCandidate(m.id)}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label size="small" colorType="primary">
              Name (optional)
            </Label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q4 model upgrade"
              className="mt-1 w-full rounded border border-border-subtle bg-bg-default px-2 py-1.5 text-sm"
            />
          </div>
          <Button
            text={starting ? "Creating…" : "Start testing"}
            appearance="primary"
            className="self-start rounded-sm"
            disabled={!ready || starting}
            onClick={handleStart}
          />
        </div>
      </div>
    </section>
  );
};
